import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { activitiesApi, reviewsApi, usersApi } from '../lib/api';
import { getCategoryInfo, formatDate, formatPrice, getNextDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  MapPin, 
  Calendar, 
  Clock, 
  Users, 
  Share2, 
  Heart, 
  Star,
  ChevronLeft,
  ChevronRight,
  Upload,
  Check,
  X,
  MessageCircle,
  UserCheck
} from 'lucide-react';
import analytics from '../lib/analytics';

const ActivityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const { t, language } = useLanguage();
  
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [selectedDateIndex, setSelectedDateIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelInfo, setCancelInfo] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [isLiked, setIsLiked] = useState(false);
  const [showWaitlistConfirm, setShowWaitlistConfirm] = useState(false);

  const fetchActivity = useCallback(async () => {
    try {
      const response = await activitiesApi.getById(id);
      setActivity(response.data);
      analytics.viewActivity(response.data);
    } catch (error) {
      toast.error(t('msg.error'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  // Track view
  useEffect(() => {
    if (id) {
      activitiesApi.trackView(id).catch(() => {});
    }
  }, [id]);

  // Auto-open review dialog if ?review=true
  useEffect(() => {
    if (searchParams.get('review') === 'true' && activity && !loading) {
      setShowReviewDialog(true);
    }
  }, [searchParams, activity, loading]);

  const handleRegister = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (user?.role === 'creator') {
      toast.error('Los creadores no pueden registrarse a actividades');
      return;
    }
    
    if (!user?.email_verified) {
      toast.error('Verifica tu correo electrónico para registrarte');
      return;
    }

    // If activity is full, register for waitlist directly (no checkout)
    if (isFull) {
      setRegistering(true);
      try {
        await activitiesApi.register(id, { quantity: 1 });
        setShowWaitlistConfirm(true);
        analytics.joinWaitlist(id);
        fetchActivity();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al registrarse');
      } finally {
        setRegistering(false);
      }
      return;
    }

    // Navigate to checkout page with selected date
    navigate(`/checkout/${id}`, { 
      state: { selectedDateIndex } 
    });
  };

  const handleCancelRegistration = () => {
    // Calculate refund info before showing modal
    const rules = activity.cancellation_rules || {};
    const rulesList = rules.rules || [{ min_hours: 48, refund_percent: 100 }, { min_hours: 24, refund_percent: 50 }];
    const minHours = rules.min_cancel_hours || 24;
    
    const selDate = activity.dates?.[selectedDateIndex] || activity.dates?.[0] || {};
    const dateStr = selDate.date || '';
    const timeStr = selDate.time || '10:00';
    
    let hoursUntil = 999;
    if (dateStr) {
      try {
        const actDt = new Date(`${dateStr}T${timeStr}:00`);
        hoursUntil = Math.max(0, (actDt - new Date()) / (1000 * 60 * 60));
      } catch (err) {
        console.warn('Date parse error:', err.message);
      }
    }
    
    // Determine refund percent
    let refundPercent = 0;
    if (hoursUntil < minHours) {
      setCancelInfo({ blocked: true, minHours, hoursUntil: Math.round(hoursUntil) });
      setShowCancelModal(true);
      return;
    }
    
    const sorted = [...rulesList].sort((a, b) => b.min_hours - a.min_hours);
    for (const rule of sorted) {
      if (hoursUntil >= rule.min_hours) {
        refundPercent = rule.refund_percent;
        break;
      }
    }
    
    const isPaid = activity.user_payment_status === 'confirmed';
    const paidAmount = activity.user_registration?.final_price || activity.price || 0;
    const refundAmount = isPaid ? Math.round(paidAmount * refundPercent / 100) : 0;
    
    setCancelInfo({ blocked: false, refundPercent, refundAmount, paidAmount, isPaid, hoursUntil: Math.round(hoursUntil) });
    setShowCancelModal(true);
  };

  const confirmCancelRegistration = async () => {
    try {
      const res = await activitiesApi.cancelRegistration(id);
      setShowCancelModal(false);
      if (cancelInfo?.isPaid && res.data?.refund_amount > 0) {
        toast.success(`Reserva cancelada. Reembolso de $${res.data.refund_amount} MXN (${res.data.refund_percent}%) en proceso.`);
      } else {
        toast.success('Reserva cancelada');
      }
      fetchActivity();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('msg.error'));
    }
  };

  const handleFollow = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    try {
      await usersApi.follow(activity.creator_id);
      fetchActivity();
    } catch (error) {
      toast.error(t('msg.error'));
    }
  };

  const handleSubmitReview = async () => {
    try {
      await reviewsApi.create({
        activity_id: id,
        activity_title: activity.title,
        rating: reviewRating,
        comment: reviewComment
      });
      toast.success(t('msg.success'));
      setShowReviewDialog(false);
      setReviewComment('');
      setReviewRating(5);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('msg.error'));
    }
  };

  const handleShare = async () => {
    const API = process.env.REACT_APP_BACKEND_URL;
    const ogUrl = `${API}/api/og/activity/${id}`;
    const spaUrl = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: activity.title,
          text: activity.short_description,
          url: ogUrl
        });
        analytics.shareActivity(id, 'native_share');
      } catch (error) { console.debug('Share cancelled:', error.message); }
    } else {
      await navigator.clipboard.writeText(ogUrl);
      analytics.shareActivity(id, 'clipboard');
      toast.success(t('msg.copied'));
    }
  };

  // Set page meta for social sharing
  useEffect(() => {
    if (activity) {
      document.title = `${activity.title} - utentik`;
      
      // Update or create Open Graph meta tags
      const setMeta = (property, content) => {
        let meta = document.querySelector(`meta[property="${property}"]`);
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute('property', property);
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      };
      
      setMeta('og:title', activity.title);
      setMeta('og:description', activity.short_description || '');
      setMeta('og:image', activity.cover_image || '');
      setMeta('og:url', window.location.href);
      setMeta('og:type', 'website');
    }
    return () => { document.title = 'utentik'; };
  }, [activity]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-400">{t('msg.error')}</p>
      </div>
    );
  }

  const categoryInfo = getCategoryInfo(activity.category);
  const CategoryIcon = categoryInfo.icon;
  const selectedDate = activity.dates?.[selectedDateIndex] || {};
  const dateSpotsLeft = selectedDate.spots_left ?? (activity.capacity - activity.registered_count);
  const dateIsFull = dateSpotsLeft <= 0;
  const spotsLeft = activity.capacity - activity.registered_count;
  const isFull = spotsLeft <= 0;
  const price = formatPrice(activity.price, language === 'es' ? 'es-MX' : 'en-US');
  
  // Build images array
  const images = [activity.cover_image, ...(activity.gallery || [])].filter(Boolean);
  if (images.length === 0) {
    images.push('https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=1200&q=80');
  }

  return (
    <div className="min-h-screen pb-32 overflow-x-hidden">
      {/* Mobile Header */}
      <div className="fixed top-0 left-0 right-0 z-50 md:hidden">
        <div className="flex items-center justify-between p-4 safe-top">
          <button 
            onClick={() => {
              if (activity?.user_payment_proof_sent) {
                navigate('/my-activities', { replace: true });
              } else {
                navigate(-1);
              }
            }}
            className="w-9 h-9 flex items-center justify-center bg-white rounded-full shadow-md"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex gap-2">
            <button 
              onClick={handleShare}
              className="w-9 h-9 flex items-center justify-center bg-white rounded-full shadow-md"
              data-testid="share-btn"
            >
              <Upload className="w-4 h-4" />
            </button>
            <button 
              onClick={() => setIsLiked(!isLiked)}
              className="w-9 h-9 flex items-center justify-center bg-white rounded-full shadow-md"
            >
              <Heart className={`w-4 h-4 ${isLiked ? 'fill-red-500 text-red-500' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden md:block max-w-5xl mx-auto px-6 pt-6 pb-4">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => {
              if (activity?.user_payment_proof_sent) {
                navigate('/my-activities', { replace: true });
              } else {
                navigate(-1);
              }
            }}
            className="flex items-center gap-2 text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="back-btn-desktop"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Volver</span>
          </button>
          <div className="flex gap-2">
            <button 
              onClick={handleShare}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm text-gray-500 transition-colors"
              data-testid="share-btn-desktop"
            >
              <Upload className="w-4 h-4" />
              Compartir
            </button>
            <button 
              onClick={() => setIsLiked(!isLiked)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm text-gray-500 transition-colors"
            >
              <Heart className={`w-4 h-4 ${isLiked ? 'fill-red-500 text-red-500' : ''}`} />
              Guardar
            </button>
          </div>
        </div>
      </div>

      {/* Image Gallery */}
      <div className="relative max-w-5xl mx-auto md:px-6">
        {/* Mobile: Horizontal scroll slideshow */}
        <div className="md:hidden relative bg-gray-100 overflow-hidden"
          onClick={() => setShowGalleryModal(true)}
        >
          <div 
            className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
            style={{ WebkitOverflowScrolling: 'touch' }}
            onScroll={(e) => {
              const container = e.target;
              const scrollPos = container.scrollLeft;
              const itemWidth = container.offsetWidth;
              const newIndex = Math.round(scrollPos / itemWidth);
              if (newIndex !== currentImageIndex && newIndex >= 0 && newIndex < images.length) {
                setCurrentImageIndex(newIndex);
              }
            }}
          >
            {images.map((img, idx) => (
              <div key={img || `img-${idx}`} className="flex-shrink-0 w-full aspect-[4/3] snap-center">
                <img 
                  src={img} 
                  alt={`${activity.title} ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>

          {images.length > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {images.map((_, idx) => (
                <div
                  key={`dot-${idx}`}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentImageIndex ? 'bg-white w-5' : 'bg-white/60'
                  }`}
                />
              ))}
            </div>
          )}

          <div className="absolute bottom-3 right-3 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded">
            {currentImageIndex + 1} / {images.length}
          </div>
        </div>

        {/* Desktop: Airbnb-style grid */}
        <div 
          className="hidden md:grid rounded-2xl overflow-hidden cursor-pointer gap-1"
          style={{
            gridTemplateColumns: images.length >= 5 ? '2fr 1fr 1fr' : images.length >= 3 ? '1fr 1fr' : '1fr',
            gridTemplateRows: images.length >= 3 ? '190px 190px' : '380px',
            height: '380px'
          }}
          onClick={() => setShowGalleryModal(true)}
          data-testid="desktop-gallery"
        >
          {/* Main large image - spans 2 rows */}
          <div 
            className="relative bg-gray-100 overflow-hidden"
            style={{ gridRow: images.length >= 3 ? '1 / 3' : '1' }}
          >
            <img 
              src={images[0]} 
              alt={activity.title}
              className="w-full h-full object-cover hover:opacity-95 transition-opacity"
              onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=800&q=80'; }}
            />
          </div>
          {/* Secondary images (up to 4) in 2x2 grid */}
          {images.slice(1, 5).map((img, idx) => (
            <div key={img} className="relative bg-gray-100 overflow-hidden">
              <img 
                src={img} 
                alt={`${activity.title} ${idx + 2}`}
                className="w-full h-full object-cover hover:opacity-95 transition-opacity"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              {/* "Ver todas" button on last visible image if more exist */}
              {idx === Math.min(images.length - 2, 3) && images.length > 5 && (
                <div className="absolute bottom-3 right-3">
                  <button className="bg-white text-gray-800 text-sm font-medium px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-shadow border border-gray-200" data-testid="show-all-photos-btn">
                    {t('common.showAllPhotos')}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Image size recommendation (subtle) */}
        {/* Recommended: Cover 1200x800px, Gallery 800x600px, min 3 images for best desktop grid */}
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        {/* Title section */}
        <div className="py-6 border-b border-gray-100">
          <h1 className="font-rubik text-2xl md:text-3xl font-bold text-gray-500 mb-2">
            {activity.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-400">
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 fill-current" />
              <span>{activity.creator?.avg_rating || 'Nuevo'}</span>
            </div>
            <span>·</span>
            <span style={{ color: categoryInfo.color }}>{t(`category.${activity.category?.toLowerCase()}`)}</span>
            <span>·</span>
            <span>{activity.city}</span>
          </div>
        </div>

        {/* Creator section */}
        <div className="py-6 border-b border-gray-100">
          <Link 
            to={`/creator/${activity.creator_slug || activity.creator_id}`}
            className="flex items-center justify-between"
            data-testid="creator-link"
          >
            <div className="flex items-center gap-4">
              <Avatar className="h-12 w-12">
                <AvatarImage src={activity.creator?.profile_image} />
                <AvatarFallback className="bg-gray-500 text-white">
                  {activity.creator?.name?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium text-gray-500">Organizado por {activity.creator?.name}</p>
                {activity.creator?.avg_rating > 0 ? (
                  <Link
                    to={`/creator/${activity.creator_slug || activity.creator_id}?tab=reviews`}
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-1 mt-0.5 hover:underline"
                    data-testid="creator-reviews-link"
                  >
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map(s => (
                        <Star
                          key={s}
                          className={`w-3.5 h-3.5 ${s <= Math.round(activity.creator.avg_rating) ? 'text-amber-500 fill-current' : 'text-gray-200'}`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-gray-500 font-medium ml-0.5">{activity.creator.avg_rating}</span>
                    <span className="text-xs text-gray-400">({activity.creator.reviews_count} {activity.creator.reviews_count === 1 ? 'reseña' : 'reseñas'})</span>
                  </Link>
                ) : (
                  <p className="text-xs text-gray-400 mt-0.5">Sin reseñas</p>
                )}
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-300" />
          </Link>
          {isAuthenticated && user?.role !== 'creator' && user?.user_id !== activity.creator_id && (
            <button
              onClick={() => navigate(`/messages?to=${activity.creator_id}&activity=${activity.activity_id}`)}
              className="mt-3 flex items-center gap-2 text-sm text-[#7f9df1] hover:underline"
              data-testid="contact-creator-btn"
            >
              <MessageCircle className="w-4 h-4" />
              Contactar al creador
            </button>
          )}
        </div>

        {/* Description */}
        <div className="py-6 border-b border-gray-100">
          <h2 className="font-rubik font-semibold text-lg mb-3">{t('activity.about')}</h2>
          <p className="text-gray-400 leading-relaxed whitespace-pre-wrap">
            {activity.long_description || activity.short_description}
          </p>
          {activity.tags && activity.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-4" data-testid="activity-tags">
              {activity.tags.map((tag, idx) => (
                <span key={tag} className="px-2.5 py-1 bg-gray-100 rounded-full text-xs text-gray-500">
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Date & Time */}
        <div className="py-6 border-b border-gray-100">
          <h2 className="font-rubik font-semibold text-lg mb-4">Fecha y horario</h2>
          
          {activity.dates.length > 1 ? (
            <div className="flex flex-wrap gap-2">
              {activity.dates.map((dateInfo, idx) => {
                const dFull = (dateInfo.spots_left ?? 1) <= 0;
                return (
                <button
                  key={dateInfo.date + dateInfo.time}
                  onClick={() => setSelectedDateIndex(idx)}
                  className={`px-4 py-3 rounded-xl border text-sm transition-all ${
                    dFull
                      ? selectedDateIndex === idx
                        ? 'border-gray-600 bg-gray-700 text-white'
                        : 'border-gray-300 bg-gray-600 text-gray-200'
                      : selectedDateIndex === idx 
                        ? 'border-gray-500 bg-gray-50' 
                        : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`date-option-${idx}`}
                >
                  <p className="font-medium">{formatDate(dateInfo.date, language === 'es' ? 'es-MX' : 'en-US')}</p>
                  <p className={dFull ? 'text-gray-300' : 'text-gray-400'}>{dateInfo.start_time} {dateInfo.end_time && `- ${dateInfo.end_time}`}</p>
                  {dFull && <p className="text-xs font-semibold mt-1 text-red-300">Cupo lleno</p>}
                </button>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-gray-400" />
              </div>
              <div>
                <p className="font-medium">{formatDate(activity.dates[0]?.date, language === 'es' ? 'es-MX' : 'en-US')}</p>
                <p className="text-sm text-gray-400">
                  {activity.dates[0]?.start_time} {activity.dates[0]?.end_time && `- ${activity.dates[0].end_time}`}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Location */}
        <div className="py-6 border-b border-gray-100">
          <h2 className="font-rubik font-semibold text-lg mb-4">Ubicación</h2>
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-5 h-5 text-gray-400" />
            </div>
            <div className="flex-1">
              <p className="font-medium">{activity.address}</p>
              <p className="text-sm text-gray-400">{activity.city}</p>
              {/* Mobile: Open in Maps app */}
              <div className="flex gap-2 mt-3 md:hidden">
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(activity.address + (activity.city ? ', ' + activity.city : ''))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-xs font-medium text-gray-600 hover:bg-gray-200 transition-colors"
                  data-testid="open-google-maps"
                >
                  <MapPin className="w-3.5 h-3.5" />
                  Google Maps
                </a>
                <a
                  href={`https://waze.com/ul?q=${encodeURIComponent(activity.address + (activity.city ? ', ' + activity.city : ''))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-xs font-medium text-gray-600 hover:bg-gray-200 transition-colors"
                  data-testid="open-waze"
                >
                  <MapPin className="w-3.5 h-3.5" />
                  Waze
                </a>
              </div>
            </div>
          </div>
          {/* Map embed on desktop */}
          {activity.address && (
            <div className="hidden md:block mt-4 rounded-xl overflow-hidden border border-gray-100" data-testid="activity-map">
              <iframe
                title="Ubicación de la actividad"
                width="100%"
                height="280"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(activity.address + (activity.city ? ', ' + activity.city : ''))}&output=embed&z=15`}
              />
            </div>
          )}
        </div>

        {/* Capacity & Age */}
        <div className="py-6 border-b border-gray-100">
          <h2 className="font-rubik font-semibold text-lg mb-4">Detalles</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-gray-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Cupo</p>
                <p className="font-medium">
                  {dateIsFull ? 'Lleno' : `${dateSpotsLeft} disponibles`}
                </p>
              </div>
            </div>
            {activity.min_age && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-gray-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-400">{t('activity.minAge')}</p>
                  <p className="font-medium">{activity.min_age} {t('activity.years')}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* FAQs */}
        {activity.faqs && activity.faqs.length > 0 && (
          <div className="py-6 border-b border-gray-100">
            <h2 className="font-rubik font-semibold text-lg mb-4">{t('activity.faq')}</h2>
            <Accordion type="multiple" className="w-full">
              {activity.faqs.map((faq, idx) => (
                <AccordionItem key={faq.question || idx} value={`faq-${idx}`} className="border-b border-gray-100">
                  <AccordionTrigger className="text-left py-4 hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400 pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}

        {/* Review button - only after activity date AND payment confirmed, NOT for creators */}
        {user?.role !== 'creator' && activity.user_registered && !activity.user_waitlist && (() => {
          const lastDate = activity.dates?.[activity.dates.length - 1]?.date;
          const isPast = lastDate && new Date(lastDate) < new Date();
          const paymentOk = !activity.price || activity.price === 0 || activity.user_payment_status === 'confirmed';
          return isPast && paymentOk ? (
            <div className="py-6">
              <Button 
                variant="outline" 
                className="w-full rounded-xl py-6" 
                onClick={() => setShowReviewDialog(true)}
                data-testid="write-review-btn"
              >
                <Star className="w-5 h-5 mr-2" />
                Escribir una reseña
              </Button>
            </div>
          ) : null;
        })()}
      </div>

      {/* Fixed bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 pt-4 pb-8 safe-bottom z-40">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
          <div className="flex-shrink-0">
            {price ? (
              <>
                <p className="font-rubik text-lg font-bold leading-tight">{price}</p>
                <p className="text-xs text-gray-400">{t('common.perPerson')}</p>
              </>
            ) : (
              <p className="font-rubik text-lg font-bold text-primary">{t('activity.free')}</p>
            )}
          </div>
          
          {user?.role === 'creator' ? (
            <span className="text-sm text-gray-400 italic">{t('activity.onlyView')}</span>
          ) : activity.user_cancelled ? (
            (() => {
              const firstDate = activity.dates?.[0]?.date;
              const activityDatePast = firstDate && new Date(firstDate) < new Date(new Date().toDateString());
              const isPaymentTimeout = activity.user_cancel_reason === 'payment_timeout_48h';
              
              if (activityDatePast && isPaymentTimeout) {
                // Scenario 2: Payment timeout + date passed
                return (
                  <span className="text-sm text-gray-400 text-right leading-snug" data-testid="cancelled-payment-past">
                    Reserva cancelada por falta de pago
                  </span>
                );
              } else if (activityDatePast) {
                // Scenario 3: Date passed (any reason)
                return (
                  <span className="text-sm text-gray-400 text-right leading-snug" data-testid="cancelled-activity-past">
                    Reserva cancelada porque la actividad ya pasó
                  </span>
                );
              } else {
                // Scenario 1: Cancelled but date still valid → re-book
                return (
                  <div className="flex flex-col items-end gap-1.5">
                    <p className="text-[10px] text-red-500 leading-tight">Reserva cancelada tras 48h sin confirmación</p>
                    <Button 
                      onClick={handleRegister}
                      className="bg-primary hover:bg-primary-hover text-white rounded-xl px-5 py-2 h-auto font-semibold text-xs"
                      data-testid="rebook-btn"
                    >
                      Reservar de nuevo
                    </Button>
                  </div>
                );
              }
            })()
          ) : activity.user_registered ? (
            <div className="flex gap-2">
              {activity.user_waitlist ? (
                <span className="px-6 py-3 bg-amber-100 text-amber-700 rounded-xl font-medium">
                  En lista de espera
                </span>
              ) : activity.user_payment_status === 'pending_payment' || activity.user_payment_status === 'payment_overdue' ? (
                activity.user_payment_proof_sent ? (
                  <div className="flex-1 flex items-center justify-end" data-testid="pending-proof-confirmation">
                    <span className="px-4 py-2.5 bg-blue-50 text-blue-600 rounded-xl text-xs font-medium leading-snug text-center">
                      Pendiente confirmación de pago por el organizador
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-col items-end gap-1.5">
                    <p className={`text-[10px] leading-tight ${activity.user_payment_status === 'payment_overdue' ? 'text-red-500' : 'text-amber-600'}`}>
                      {activity.user_payment_status === 'payment_overdue' ? 'Plazo vencido — 24h de gracia' : 'Tienes 24h para realizar tu pago'}
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline"
                        onClick={handleCancelRegistration}
                        className="rounded-xl px-3 py-2 h-auto text-gray-400 text-xs"
                        data-testid="cancel-reservation-btn"
                      >
                        Cancelar
                      </Button>
                      <Button 
                        onClick={() => navigate(`/payment-proof/${activity.activity_id}?creator=${activity.creator_id}`, { replace: true })}
                        className="bg-primary hover:bg-primary-hover text-white rounded-xl px-4 py-2 h-auto font-semibold text-xs"
                        data-testid="confirm-payment-btn"
                      >
                        Confirmar pago
                      </Button>
                    </div>
                  </div>
                )
              ) : activity.user_payment_status === 'confirmed' ? (
                <span className="px-6 py-3 bg-green-100 text-green-700 rounded-xl font-medium" data-testid="payment-confirmed-badge">
                  Reserva confirmada
                </span>
              ) : (
                <Button 
                  variant="outline"
                  onClick={handleCancelRegistration}
                  className="rounded-xl px-6 py-3 h-auto"
                  data-testid="cancel-registration-btn"
                >
                  Cancelar
                </Button>
              )}
            </div>
          ) : (() => {
            const firstDate = activity.dates?.[0]?.date;
            const activityDatePast = firstDate && new Date(firstDate) < new Date(new Date().toDateString());
            if (activityDatePast) {
              return (
                <span className="text-sm text-gray-400 italic" data-testid="activity-past-label">
                  Esta actividad ya pasó
                </span>
              );
            }
            return (
              <Button 
                onClick={handleRegister}
                disabled={registering}
                className="bg-primary hover:bg-primary-hover text-white rounded-xl px-8 py-3 h-auto font-semibold"
                data-testid="reserve-btn"
              >
                {registering ? 'Procesando...' : (isFull ? t('activity.waitlist') : t('activity.reserve'))}
              </Button>
            );
          })()}
        </div>
      </div>

      {/* Waitlist Confirmation Dialog */}
      <Dialog open={showWaitlistConfirm} onOpenChange={setShowWaitlistConfirm}>
        <DialogContent className="max-w-md mx-4 p-0 rounded-2xl overflow-hidden" data-testid="waitlist-confirm-dialog">
          {/* Activity image */}
          <div className="relative h-44 overflow-hidden">
            <img 
              src={activity?.cover_image || 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=600'} 
              alt={activity?.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <button
              onClick={() => setShowWaitlistConfirm(false)}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-white/80 rounded-full"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-6 text-center">
            {/* Success icon */}
            <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
              <Check className="w-7 h-7 text-green-500" />
            </div>

            <h3 className="font-rubik text-lg font-bold text-gray-500 mb-2" data-testid="waitlist-confirm-title">
              Tu lugar en la lista de espera ha sido confirmado
            </h3>

            {/* Activity info */}
            <div className="bg-gray-50 rounded-xl p-4 mb-4 text-left">
              <p className="font-medium text-sm text-gray-600">{activity?.title}</p>
              <p className="text-xs text-gray-400 mt-1">
                {activity?.dates?.[0]?.date && new Date(activity.dates[0].date + 'T12:00:00').toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
                {activity?.dates?.[0]?.start_time && ` · ${activity.dates[0].start_time}`}
              </p>
              <p className="text-xs text-gray-400">{activity?.city}</p>
            </div>

            <p className="text-sm text-gray-400 leading-relaxed mb-6">
              Cuando el creador publique una nueva fecha u horario, te llegará una notificación para que reserves tu lugar.
            </p>

            <Button 
              onClick={() => setShowWaitlistConfirm(false)}
              className="w-full bg-primary hover:bg-primary-hover rounded-xl py-5 font-semibold"
              data-testid="waitlist-confirm-ok-btn"
            >
              Entendido
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Gallery Modal */}
      <Dialog open={showGalleryModal} onOpenChange={setShowGalleryModal}>
        <DialogContent className="max-w-4xl h-[90vh] p-0 bg-black">
          <button 
            onClick={() => setShowGalleryModal(false)}
            className="absolute top-4 right-4 z-50 w-10 h-10 flex items-center justify-center bg-white/10 rounded-full text-white hover:bg-white/20"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="relative h-full flex items-center justify-center">
            <img 
              src={images[currentImageIndex]} 
              alt=""
              className="max-w-full max-h-full object-contain"
            />
            {images.length > 1 && (
              <>
                <button 
                  onClick={() => setCurrentImageIndex(prev => prev > 0 ? prev - 1 : images.length - 1)}
                  className="absolute left-4 w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-lg"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button 
                  onClick={() => setCurrentImageIndex(prev => prev < images.length - 1 ? prev + 1 : 0)}
                  className="absolute right-4 w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-lg"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-rubik">¿Cómo fue tu experiencia?</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-4">
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setReviewRating(star)}
                  className="p-1"
                  data-testid={`rating-star-${star}`}
                >
                  <Star 
                    className={`w-10 h-10 ${star <= reviewRating ? 'text-amber-500 fill-current' : 'text-gray-200'}`} 
                  />
                </button>
              ))}
            </div>
            <Textarea
              placeholder="Cuéntanos más sobre tu experiencia..."
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
              rows={4}
              className="rounded-xl"
              data-testid="review-comment"
            />
            <Button 
              onClick={handleSubmitReview} 
              className="w-full bg-primary hover:bg-primary-hover rounded-xl py-6"
              data-testid="submit-review-btn"
            >
              Publicar reseña
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Registration Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{cancelInfo?.blocked ? 'Cancelación no disponible' : 'Cancelar reserva'}</DialogTitle>
          </DialogHeader>
          {cancelInfo?.blocked ? (
            <div className="py-4 space-y-3">
              <div className="p-4 bg-red-50 rounded-xl">
                <p className="text-sm text-red-700">
                  No es posible cancelar. De acuerdo a la política de cancelación, no se permiten cancelaciones con menos de <strong>{cancelInfo.minHours}h</strong> de anticipación.
                </p>
                <p className="text-xs text-red-500 mt-2">Faltan ~{cancelInfo.hoursUntil}h para la actividad.</p>
              </div>
              <Button variant="outline" className="w-full" onClick={() => setShowCancelModal(false)}>Entendido</Button>
            </div>
          ) : (
            <div className="py-4 space-y-4">
              {cancelInfo?.isPaid ? (
                <div className="p-4 bg-amber-50 rounded-xl space-y-2">
                  <p className="text-sm text-amber-800 font-medium">Información de reembolso</p>
                  <div className="text-sm text-amber-700 space-y-1">
                    <p>Monto pagado: <strong>${cancelInfo.paidAmount} MXN</strong></p>
                    <p>Reembolso: <strong>{cancelInfo.refundPercent}%</strong> = <strong>${cancelInfo.refundAmount} MXN</strong></p>
                    <p className="text-xs text-amber-500">Faltan ~{cancelInfo.hoursUntil}h para la actividad</p>
                  </div>
                  {cancelInfo.refundPercent < 100 && (
                    <p className="text-xs text-amber-600 mt-1">El porcentaje de reembolso se calcula según la política de cancelación del creador.</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-600">¿Estás seguro de que deseas cancelar tu reserva?</p>
              )}
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowCancelModal(false)}>Volver</Button>
                <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={confirmCancelRegistration}>
                  Confirmar cancelación
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActivityDetail;
